import { useState, useEffect, useCallback } from 'react';
import {
  IconChevronRightFilled,
  IconSearchFilled,
  IconPaperBagOff,
} from '@tabler/icons-react';
import Busqueda from '../components/Busqueda';
import TablaArticulos from '../components/TablaArticulos';
import GloboFlotante from '../components/GloboFlotante';
import EmptyState from '../components/EmptyState';
import EditarAzulButton from '../Utils/EditarAzulButton';
import TipBlue from '../Utils/TipBlue';
import { ensureMinLoading } from '../Utils/withLoading';
import ModalDetallesProducto from '../components/ModalDetallesProducto';
import ModalAgregarProducto from '../components/ModalAgregarProducto';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';

function formatPrice(price) {
  return '$' + (Number(price) || 0).toFixed(2);
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function TablaSkeleton() {
  return (
    <div className="flex flex-col gap-1.5" role="status" aria-label="Cargando artículos">
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 rounded-2xl animate-pulse"
          style={{ height: '64px', padding: '0 12px' }}
        >
          <span
            className="shrink-0 rounded-full"
            style={{ width: '22px', height: '22px', backgroundColor: '#141414' }}
          />
          <span
            className="rounded-full"
            style={{ width: '30%', height: '12px', backgroundColor: '#141414' }}
          />
          <span
            className="rounded-full"
            style={{ width: '14%', height: '12px', backgroundColor: '#141414' }}
          />
          <span
            className="ml-auto rounded-full"
            style={{ width: '11%', height: '12px', backgroundColor: '#141414' }}
          />
        </div>
      ))}
    </div>
  );
}

function SinResultados({ query, onClear }) {
  return (
    <EmptyState
      icon={IconSearchFilled}
      title="Sin resultados"
      description={`No encontramos artículos para “${query}”. Prueba con otro nombre, categoría o variante.`}
      action={
        <button
          type="button"
          onClick={onClear}
          className="flex items-center justify-center h-11 px-5 mt-3 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.1]"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            fontSize: '14px',
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: FONT,
          }}
        >
          Limpiar búsqueda
        </button>
      }
    />
  );
}

export default function GestionArticulos({ dynamicIslandRef }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchProducts = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const res = await apiFetch(`/products`);
      if (!res.ok) throw new Error('Error al cargar productos');
      const data = await res.json();
      setProducts(data);
    } catch (e) {
      if (dynamicIslandRef?.current) {
        dynamicIslandRef.current.addAlert('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
      }
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [dynamicIslandRef]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleDelete = async (ids) => {
    const startedAt = Date.now();
    if (dynamicIslandRef?.current) {
      const msg = ids.length === 1 ? 'Eliminando artículo...' : `Eliminando ${ids.length} artículos...`;
      dynamicIslandRef.current.addAlert('loading', msg);
    }
    let failed = 0;
    try {
      const results = await Promise.all(ids.map((id) =>
        apiFetch(`/products/${id}`, { method: 'DELETE' })
      ));
      failed = results.filter((r) => !r.ok).length;
    } catch {
      failed = ids.length;
    }
    await ensureMinLoading(startedAt);
    if (dynamicIslandRef?.current) {
      dynamicIslandRef.current.clearAlerts();
      if (failed === 0) {
        const msg = ids.length === 1
          ? 'Artículo eliminado correctamente'
          : `${ids.length} artículos eliminados correctamente`;
        dynamicIslandRef.current.addAlert('success', msg);
      } else if (failed < ids.length) {
        dynamicIslandRef.current.addAlert('error', `${failed} ${failed === 1 ? 'artículo no pudo' : 'artículos no pudieron'} ser eliminados`);
      } else {
        dynamicIslandRef.current.addAlert('error', 'Error al eliminar artículos');
      }
    }
    fetchProducts(false);
  };

  const rows = products.map((p) => {
    const categories = p.categories || (p.category_id ? [{ id: p.category_id, name: p.category_name }] : []);
    return {
      id: p.id,
      nombre: p.name,
      category_id: categories.length > 0 ? categories[0].id : p.category_id,
      categories,
      categoria: categories.map((c) => c.name).join(', ') || p.category_name,
      price: p.price,
      precio: formatPrice(p.price),
      variant_groups: p.variant_groups || [],
      variantes: (p.variant_groups || []).map((g) => g.name).join(', ') || '',
      imagenes: p.img_url ? [p.img_url] : [],
      descripcion: p.description || '',
      receta: '',
      destacado: false,
    };
  });

  const normalizedQuery = normalizeText(query.trim());
  const filteredRows = normalizedQuery
    ? rows.filter((row) =>
        [row.nombre, row.categoria, row.variantes, row.id]
          .some((value) => normalizeText(value).includes(normalizedQuery))
      )
    : rows;

  const categoryBadge = (name) => (
    <span
      className="inline-flex items-center justify-center"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        borderRadius: '9999px',
        height: '30px',
        padding: '0 12px',
        fontSize: '13px',
        fontWeight: 600,
        fontFamily: FONT,
        color: 'rgba(255, 255, 255, 0.8)',
        whiteSpace: 'nowrap',
      }}
    >
      {name}
    </span>
  );

  const verMasTrigger = () => (
    <span className="inline-flex items-center py-1.5 -my-1.5">
      <span
        className="inline-flex items-center justify-center gap-1"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          borderRadius: '9999px',
          height: '30px',
          padding: '0 10px 0 12px',
          fontSize: '13px',
          fontWeight: 600,
          fontFamily: FONT,
          color: 'rgba(255, 255, 255, 0.8)',
          whiteSpace: 'nowrap',
        }}
      >
        Ver más
        <IconChevronRightFilled size={12} color="rgba(255, 255, 255, 0.4)" />
      </span>
    </span>
  );

  const columns = [
    { key: 'id', label: 'ID', flex: 1, minWidth: '60px' },
    { key: 'nombre', label: 'Nombre', flex: 2, minWidth: '150px' },
    {
      key: 'categoria',
      label: 'Categoría',
      flex: 1,
      minWidth: '105px',
      render: (row) => {
        const names = row.categories.map((c) => c.name);
        if (names.length === 0) {
          return (
            <span style={{ fontSize: '13px', fontWeight: 500, fontFamily: FONT, color: 'rgba(255, 255, 255, 0.3)' }}>
              Sin categoría
            </span>
          );
        }
        if (names.length === 1) return categoryBadge(names[0]);
        return (
          <GloboFlotante trigger={verMasTrigger()}>
            <div className="flex flex-wrap gap-1.5" style={{ maxWidth: '300px' }}>
              {names.map((name) => categoryBadge(name))}
            </div>
          </GloboFlotante>
        );
      },
    },
    { key: 'precio', label: 'Precio', flex: 1, minWidth: '85px' },
    {
      key: 'variantes',
      label: 'Variantes',
      flex: 1,
      minWidth: '100px',
      render: (row) => {
        const groups = row.variant_groups;
        if (groups.length === 0) {
          return (
            <span style={{ fontSize: '13px', fontWeight: 500, fontFamily: FONT, color: 'rgba(255, 255, 255, 0.3)' }}>
              Sin variantes
            </span>
          );
        }
        if (groups.length === 1) return categoryBadge(groups[0].name);
        return (
          <GloboFlotante trigger={verMasTrigger()}>
            <div className="flex flex-wrap gap-1.5" style={{ maxWidth: '300px' }}>
              {groups.map((g) => categoryBadge(g.name))}
            </div>
          </GloboFlotante>
        );
      },
    },
    { key: 'editar', label: '', flex: 0, minWidth: '92px', render: (row) => <EditarAzulButton size="lg" onClick={() => setSelectedProduct(row)} /> },
    { key: 'eliminar', label: '', flex: 0, minWidth: '48px' },
  ];

  const countLabel = normalizedQuery
    ? `${filteredRows.length} ${filteredRows.length === 1 ? 'resultado' : 'resultados'}`
    : `${rows.length} ${rows.length === 1 ? 'artículo' : 'artículos'}`;

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      <header className="shrink-0 px-4 lg:px-6 pt-5 pb-4 border-b border-white/[0.16]">
        <div className="min-w-0">
          <h1
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Gestión de artículos
          </h1>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Administra el catálogo de productos
          </p>
        </div>
      </header>

      <div className="flex flex-col flex-1 min-h-0 px-4 lg:px-6 pt-5 pb-4">
        <div className="flex items-center gap-3 shrink-0 pb-4">
          <div className="flex-1 max-w-[560px]">
            <Busqueda
              size="lg"
              value={query}
              onChange={setQuery}
              placeholder="Buscar artículo, categoría o variante"
            />
          </div>
          <span
            className="ml-auto shrink-0 hidden sm:inline"
            style={{
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.35)',
              fontFamily: FONT,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {loading ? 'Cargando…' : countLabel}
          </span>
        </div>

        <div className="flex-1 min-h-0" aria-busy={loading}>
          {loading ? (
            <div className="h-full overflow-y-auto pedidos-scroll">
              <TablaSkeleton />
            </div>
          ) : (
            <TablaArticulos
              columns={columns}
              rows={filteredRows}
              selectable
              onDelete={handleDelete}
              dynamicIslandRef={dynamicIslandRef}
              emptyComponent={
                normalizedQuery ? (
                  <SinResultados query={query.trim()} onClear={() => setQuery('')} />
                ) : (
                  <EmptyState
                    icon={IconPaperBagOff}
                    title="Aún no hay artículos"
                    description="Agrega productos para que tu menú no quede en el vacío."
                    className="h-full"
                  />
                )
              }
            />
          )}
        </div>
      </div>

      <div className="shrink-0 px-4 lg:px-6 py-4 border-t border-white/[0.16] flex items-center justify-between gap-4">
        <TipBlue>Usa Agregar para crear nuevos artículos y el lápiz azul para editar los existentes.</TipBlue>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="flex items-center justify-center h-12 px-8 rounded-full border-none cursor-pointer touch-manipulation select-none font-semibold transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
          style={{
            backgroundColor: '#0071e3',
            color: '#fff',
            fontSize: '15px',
            letterSpacing: '-0.01em',
            fontFamily: FONT,
          }}
        >
          Agregar
        </button>
      </div>

      <ModalDetallesProducto
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onUpdated={() => { setSelectedProduct(null); fetchProducts(false); }}
        product={selectedProduct}
        dynamicIslandRef={dynamicIslandRef}
      />
      <ModalAgregarProducto
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={() => { setShowAddModal(false); fetchProducts(false); }}
        dynamicIslandRef={dynamicIslandRef}
      />
    </div>
  );
}
